import { z } from 'zod'

type PdfJsModule = typeof import('pdfjs-dist')

export type PDFDocumentProxy = Awaited<
  ReturnType<PdfJsModule['getDocument']>['promise']
>

const configVarsEmbedSchema = z.object({
  BASE_PATH: z.string().optional(),
})

function getBasePathFromConfig(): string {
  try {
    const el = document.getElementById('config-vars-embed')
    if (!el?.textContent) return ''
    const parsed = configVarsEmbedSchema.safeParse(JSON.parse(el.textContent))
    return parsed.success ? (parsed.data.BASE_PATH ?? '') : ''
  } catch {
    return ''
  }
}

function getPdfWorkerSrc(): string {
  const base = typeof document !== 'undefined' ? getBasePathFromConfig() : ''
  const path = base ? `/${base}/pdf.worker.min.js` : '/pdf.worker.min.js'
  return `${window.location.origin}${path}`
}

let pdfJsPromise: Promise<PdfJsModule> | null = null

/** Lazy-load pdfjs-dist and set the worker src once. */
export async function getPdfJs(): Promise<PdfJsModule> {
  if (!pdfJsPromise) {
    pdfJsPromise = import('pdfjs-dist').then((pdfjsLib) => {
      if (
        typeof document !== 'undefined' &&
        !pdfjsLib.GlobalWorkerOptions.workerSrc
      ) {
        pdfjsLib.GlobalWorkerOptions.workerSrc = getPdfWorkerSrc()
      }
      return pdfjsLib
    })
  }
  return pdfJsPromise
}

/** Load a PDF document from a URL with the shared worker bootstrap. */
export async function loadPdfDocument(
  pdfUrl: string
): Promise<PDFDocumentProxy> {
  const pdfjsLib = await getPdfJs()
  const loadingTask = pdfjsLib.getDocument({ url: pdfUrl, verbosity: 0 })
  return loadingTask.promise
}
